import { type ReactNode, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as saveValidationSelectors from '@makemydeal/dr-dash-store/selectors/saveValidationSelectors';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { AccessoriesSlideOut } from './AccessoriesSlideOut';
import { AppraisalSlideOut } from './AppraisalSlideOut';
import { ChangeVehicleSlideOut } from './ChangeVehicleSlideOut';
import { FirstPencilChangeVehicleSlideOut } from './FirstPencilChangeVehicleSlideOut';
import { FirstPencilIncentivesSlideOut } from './FirstPencilIncentivesSlideOut';
import { FeesSlideOut } from './FeesSlideOut';
import { IncentivesSlideOut } from './IncentivesSlideOut';
import { LenderSlideOut } from './LenderSlideOut';
import { ProtectionProductSlideOut } from './ProtectionProductSlideOut';
import { TaxesSlideOut } from './TaxesSlideOut';
import { TradeInSlideOut } from './TradeInSlideOut';
import { useSlideoutHotkeys } from './useSlideoutHotkeys';
import { useBuyerHotkey } from './useBuyerHotkey';
import { useCoBuyerHotkey } from './useCoBuyerHotkey';
import { useClearFocusOnOutsideClick } from './useClearFocusOnOutsideClick';
import { validateSlideOutEntity } from './slideOutValidation';

// Re-export SlideOutType from baseDealActionCreators to avoid circular dependency
export const { SlideOutType } = baseDealActionCreators;

interface BaseDealSlideOutProviderProps {
    children: ReactNode;
    scopeId?: string;
}

export const BaseDealSlideOutProvider = ({ children, scopeId }: BaseDealSlideOutProviderProps) => {
    const dispatch = useDispatch();
    const store = useStore();
    const currentSlideOut = useSelector(baseDealSelectors.getCurrentSlideOut);
    const navigationScopeTarget = useSelector((state: any) => state.__navigationScopeTarget);
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const isOnSaveFlow = useSelector(saveValidationSelectors.isOnSaveValidationFlow);

    // Only register hotkeys on the primary scope instance to avoid
    // multiple registrations and unbind issues when non-primary scopes are deleted.
    const isPrimary = !scopeId || scopeId === primaryScopeId;
    useSlideoutHotkeys(isPrimary);
    useBuyerHotkey(isPrimary);
    useCoBuyerHotkey(isPrimary);
    useClearFocusOnOutsideClick(isPrimary);

    const shouldRenderSlideouts = !navigationScopeTarget || !scopeId || navigationScopeTarget === scopeId;

    const closeSlideOut = useCallback(
        (options?: { cancelled?: boolean }) => {
            dispatch(baseDealActionCreators.closeSlideOut());
            if (isOnSaveFlow && !options?.cancelled) {
                const action = validateSlideOutEntity(currentSlideOut, store.getState() as StateTree);
                if (action) {
                    dispatch(action);
                }
            }
        },
        [isOnSaveFlow, currentSlideOut, store]
    );

    return (
        <>
            {children}
            {shouldRenderSlideouts && (
                <>
                    <AccessoriesSlideOut isOpen={currentSlideOut === SlideOutType.Accessories} onClose={closeSlideOut} />
                    <AppraisalSlideOut isOpen={currentSlideOut === SlideOutType.Appraisal} onClose={closeSlideOut} />
                    <ChangeVehicleSlideOut isOpen={currentSlideOut === SlideOutType.ChangeVehicle} onClose={closeSlideOut} />
                    <FirstPencilChangeVehicleSlideOut
                        isOpen={currentSlideOut === SlideOutType.FirstPencilChangeVehicle}
                        onClose={closeSlideOut}
                    />
                    <FirstPencilIncentivesSlideOut
                        isOpen={currentSlideOut === SlideOutType.FirstPencilIncentives}
                        onClose={closeSlideOut}
                    />
                    <FeesSlideOut isOpen={currentSlideOut === SlideOutType.Fees} onClose={closeSlideOut} />
                    <IncentivesSlideOut isOpen={currentSlideOut === SlideOutType.Incentives} onClose={closeSlideOut} />
                    <LenderSlideOut isOpen={currentSlideOut === SlideOutType.Lender} onClose={closeSlideOut} />
                    <ProtectionProductSlideOut isOpen={currentSlideOut === SlideOutType.Protection} onClose={closeSlideOut} />
                    <TaxesSlideOut isOpen={currentSlideOut === SlideOutType.Taxes} onClose={closeSlideOut} />
                    <TradeInSlideOut isOpen={currentSlideOut === SlideOutType.TradeIn} onClose={closeSlideOut} />
                </>
            )}
        </>
    );
};
